/** @format */

import { Avatar, Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { getApiUrl } from '../../../../api.ts';
import { selectAuth } from '../../../../redux/authSlice';
import { useUserWebSocket } from '../../../../websocket/UserWebSocket';
import NewTooltip from '../../../misc/NewTooltip';
import {
  PLANNING_PRESENCE_MAX_VISIBLE,
  presenceNameColor,
  splitPresenceAvatars,
} from '../utils/planningPresence';

const AVATAR_SIZE = 32;
const OVERLAP_PX = 10;

function PresenceAvatar({ user, isCurrentUser }) {
  const color = presenceNameColor(user.fullName || user.initials);
  return (
    <NewTooltip content={user.fullName || user.initials} placement="bottom">
      <Box sx={{ display: 'inline-flex' }}>
        <Avatar
          alt={user.fullName || user.initials}
          src={user.hasAvatar ? `${getApiUrl()}/avatar/${user.id}` : undefined}
          sx={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: color,
            color: '#fff',
            border: '2px solid #fff',
            boxShadow: isCurrentUser
              ? '0 0 0 2px #408b6a'
              : '0 0 0 1px rgba(0,0,0,0.08)',
          }}
        >
          {user.initials}
        </Avatar>
      </Box>
    </NewTooltip>
  );
}

PresenceAvatar.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    fullName: PropTypes.string,
    initials: PropTypes.string,
    hasAvatar: PropTypes.bool,
  }).isRequired,
  isCurrentUser: PropTypes.bool,
};

PresenceAvatar.defaultProps = {
  isCurrentUser: false,
};

export default function PlanningPresenceAvatars() {
  const { user } = useSelector(selectAuth);
  const { planningPresenceUsers = [] } = useUserWebSocket() || {};
  const { visible, hidden } = splitPresenceAvatars(
    planningPresenceUsers,
    user?.id,
    PLANNING_PRESENCE_MAX_VISIBLE,
  );

  if (!visible.length) return null;

  return (
    <Box
      aria-label="Personnes connectées au planning"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        pl: 0.5,
        '& > *': {
          ml: `-${OVERLAP_PX}px`,
        },
        '& > *:first-of-type': {
          ml: 0,
        },
      }}
    >
      {visible.map((row, index) => (
        <Box
          key={row.id}
          sx={{
            position: 'relative',
            zIndex: index + 1,
          }}
        >
          <PresenceAvatar
            user={row}
            isCurrentUser={Number(row.id) === Number(user?.id)}
          />
        </Box>
      ))}
      {hidden.length ? (
        <Box sx={{ position: 'relative', zIndex: visible.length + 1 }}>
          <NewTooltip
            content={hidden.map((row) => row.fullName).join(', ')}
            placement="bottom"
          >
            <Avatar
              alt={`+${hidden.length} personnes`}
              sx={{
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: '#374151',
                color: '#fff',
                border: '2px solid #fff',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
              }}
            >
              +{hidden.length}
            </Avatar>
          </NewTooltip>
        </Box>
      ) : null}
    </Box>
  );
}
